<template>
  <div class="background-modal">
    <div class="wrapper-modal">
      <div class="wrapper-title">
        <p class="title">Información de noticia</p>
        <CloseButton @click="closeModal" />
      </div>
      <div class="wrapper-form">
        <GeneralInput
          v-model:text="title.val"
          :value="title.val"
          :label="'Título'"
          :has-error="title.enabled"
          :error="title.errors.length === 0 ? '' : title.errors[0]"
          class="title"
        />
        <GeneralTextArea
          v-model:text="description.val"
          :value="description.val"
          :label="'Noticia'"
          :has-error="description.enabled"
          :error="description.errors.length === 0 ? '' : description.errors[0]"
          class="news"
        />

        <div class="wrapper-image">
          <div class="info">
            <p>Imagen</p>
            <SecondaryButton
              :buttonLabel="'Subir imagen'"
              @click="$refs.fileImage.click()"
            />
            <input
              type="file"
              ref="fileImage"
              hidden
              @change="uploadImage"
              @click="$event.target.value = ''"
            />
          </div>
          <p>La imagen debe tener una relación 920/480.</p>
        </div>
        <div class="wrapper-news-image">
          <figure class="grid-image">
            <img v-if="showPreview" :src="previewImage" class="news-image" />
            <img v-else-if="showImage" :src="news.slider" class="news-image" />
            <p v-else class="no-image">Sin imagen</p>
          </figure>

          <DeleteButton
            v-if="showPreview"
            class="delete"
            @click="removeUploadFile"
          />
        </div>
      </div>
      <StandardButton
        :buttonLabel="'Guardar'"
        class="margin-button"
        :is-disabled="disabledForm"
        @click="createEditNews"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, watch, Ref, ref, computed } from "vue";
import { createEditNewsValidator } from "@/ux/news/validators/createEditNewsValidator";
import GeneralInput from "@/components/commons/controls/GeneralInput.vue";
import GeneralTextArea from "@/components/commons/controls/GeneralTextArea.vue";
import SecondaryButton from "@/components/commons/controls/SecondaryButton.vue";
import CloseButton from "@/components/commons/controls/CloseButton.vue";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import { News } from "@/business/news/domain/news";
import { convertToBase64 } from "@/business/shared/commons/multimedia/convertToBase64";
import DeleteButton from "@/components/commons/controls/DeleteButton.vue";

export default defineComponent({
  name: "ModalCreateEditNews",
  props: {
    news: {
      type: Object as () => News | null,
      default: null,
    },
  },
  components: {
    GeneralInput,
    GeneralTextArea,
    CloseButton,
    SecondaryButton,
    StandardButton,
    DeleteButton,
  },

  setup(props, context) {
    const { title, description, successForm, successFormEdit, reset } =
      createEditNewsValidator();

    const isEdit: Ref<boolean> = ref(false);
    const file: Ref<File | null> = ref(null);
    const showPreview: Ref<boolean> = ref(false);
    const previewImage: Ref<string> = ref("");

    function closeModal() {
      reset();
      removeUploadFile();
      context.emit("close");
    }

    watch(
      () => props.news,
      () => {
        reset();
        if (props.news != null) {
          title.value.val = props.news.title;
          description.value.val = props.news.body;
          isEdit.value = true;
        } else {
          isEdit.value = false;
        }
      }
    );

    function createNews() {
      const newNewsForm = {
        title: title.value.val,
        description: description.value.val,
        image: file.value,
      };

      context.emit("create", newNewsForm);
      removeUploadFile();
    }

    function editNews() {
      const editNewsForm = {
        title: title.value.val,
        description: description.value.val,
        image: file.value,
      };

      context.emit("edit", editNewsForm);
      removeUploadFile();
    }

    function createEditNews() {
      if (!isEdit.value) {
        createNews();
      } else {
        editNews();
      }
    }

    async function uploadImage(event: any) {
      file.value = event.target.files[0];
      if (file.value !== null) {
        previewImage.value = await convertToBase64.execute(file.value);
        showPreview.value = true;
      }
    }

    function removeUploadFile(): void {
      previewImage.value = "";
      file.value = null;
      showPreview.value = false;
    }

    const disabledForm = computed(() => {
      return isEdit.value
        ? successFormEdit.value
        : successForm.value || file.value == null;
    });

    const showImage = computed(() => isEdit.value && !showPreview.value);

    return {
      closeModal,
      title,
      description,
      disabledForm,
      reset,
      createEditNews,
      uploadImage,
      showPreview,
      showImage,
      previewImage,
      removeUploadFile,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/news/styles/modalCreateEditNewsStyle";
</style>
